// @flow

import EventNames from '../constants/EventNames';
import DataLayerEventNames from '../constants/DataLayer/EventNames';
import Filter from './abstract/Filter';
import Notifications from '../constants/Notifications';
import FilterTypes from '../constants/FilterTypes';
import FilterGroups from '../constants/FilterGroups';

export default class PriceFilter extends Filter {
  // All general properties for a range slider (in case this ever gets extracted)
  #minSlider: HTMLInputElement;
  #maxSlider: HTMLInputElement;
  #minInput: HTMLInputElement;
  #maxInput: HTMLInputElement;
  #sliderValues: Array<string>;
  #minValue: number;
  #maxValue: number;

  #updateSliderHandler: function;
  #numberInputHandler: function;
  #numberBlurHandler: function;

  // All filter properties
  #oldValues: Array<number>;
  #filterHandler: function;

  /**
   * Initialize the price filter component
   */
  bind(pEl: HTMLElement): void {
    super.bind(pEl);
    const sliders: any = pEl.querySelectorAll('input[type="range"]');
    const inputs: any = pEl.querySelectorAll('input[type="number"]');

    if (sliders.length < 2) {
      throw new Error('RangeSlider needs two range sliders');
    }
    if (inputs.length < 2) {
      throw new Error('RangeSlider needs two number input fields');
    }

    this.#minInput = inputs[0];
    this.#maxInput = inputs[1];

    this.#minSlider = sliders[0];
    this.#maxSlider = sliders[1];

    this.#sliderValues = [];
    if (this.#minSlider && this.#maxSlider) {
      const minSliderValue: string = this.#minSlider.value;
      const maxSliderValue: string = this.#maxSlider.value;
      this.#sliderValues = [minSliderValue, maxSliderValue];
    }

    this.#minValue = +(this.#minSlider.min || 0);
    this.#maxValue = +(this.#maxSlider.max || 100);

    this.#minSlider.classList.add('range', 'range__multirange', 'range__multirange--min');
    this.#maxSlider.classList.add('range', 'range__multirange', 'range__multirange--max');

    this.#minSlider.value = (this.#sliderValues[0] || this.#minValue + (this.#maxValue - this.#minValue) / 2).toString();
    this.#maxSlider.value = (this.#sliderValues[1] || this.#minValue + (this.#maxValue - this.#minValue) / 2).toString();

    this.#updateSliderHandler = this.handleSliderEvents.bind(this);
    this.#numberInputHandler = this.handleInputEvents.bind(this);
    this.#numberBlurHandler = this.handleBlurEvents.bind(this);

    this.#minSlider.addEventListener(EventNames.INPUT, this.#updateSliderHandler, true);
    this.#maxSlider.addEventListener(EventNames.INPUT, this.#updateSliderHandler, true);
    this.#minInput.addEventListener(EventNames.INPUT, this.#numberInputHandler, true);
    this.#maxInput.addEventListener(EventNames.INPUT, this.#numberInputHandler, true);
    this.#minInput.addEventListener(EventNames.BLUR, this.#numberBlurHandler, true);
    this.#maxInput.addEventListener(EventNames.BLUR, this.#numberBlurHandler, true);

    // Filter setup

    this.#oldValues = [+this.#minInput.value, +this.#maxInput.value];
    this.#filterHandler = this.handleFilter.bind(this);
    this.#minSlider.addEventListener(EventNames.CHANGE, this.#filterHandler, true);
    this.#maxSlider.addEventListener(EventNames.CHANGE, this.#filterHandler, true);
    this.addListener(Notifications.FILTERED, this.handleUpdateFilters);

    this.update(this.low, this.high);
  }

  /**
   * Update the slider
   */
  update(pLow: number, pHigh: number): void {
    const low: string = (100 * (pLow / (this.#maxValue - this.#minValue))).toString() + '%';
    const high: string = (100 * (pHigh / (this.#maxValue - this.#minValue))).toString() + '%';

    this.#maxSlider.style.setProperty('--low', low);
    this.#maxSlider.style.setProperty('--high', high);
  }

  /**
   * Executes on input events
   */
  handleInputEvents(): void {
    this.#minSlider.value = this.#minInput.value;
    this.#maxSlider.value = this.#maxInput.value;
    const low: number = parseInt(this.#minInput.value) - this.#minValue;
    const high: number = parseInt(this.#maxInput.value) - this.#minValue;

    this.update(low, high);
  }

  /**
   * Executes on blur events
   */
  handleBlurEvents(): void {
    if (parseInt(this.#minInput.value) > parseInt(this.#maxInput.value)) {
      const min: string = this.#minInput.value;
      const max: string = this.#maxInput.value;
      this.#minInput.value = max;
      this.#maxInput.value = min;
    }

    this.handleFilter();
    this.update(this.low, this.high);
  }

  /**
   * Executes when the slider is updated
   */
  handleSliderEvents(): void {
    this.#minInput.value = this.valueLow.toString();
    this.#maxInput.value = this.valueHigh.toString();

    this.update(this.low, this.high);
  }

  /**
   * Executes when the user uses the price filter
   */
  handleFilter(): void {
    this.validate();

    const min: number = +this.#minInput.value;
    const max: number = +this.#maxInput.value;

    if (!this.hasDefaultValues(min, max)) {
      window.store.url.setPrice(min, max);
    } else {
      window.store.url.removePrice();
    }

    if (this.hasChanged(min, max)) {
      this.dispatch({
        event: DataLayerEventNames.FILTER_USE,
        data: {
          filter: { name: 'Price', value: [min, max] }
        },
        GA4: true
      });

      this.sendFilterNotification();
    }

    this.#oldValues = [min, max];
  }

  /**
   * Update the filter only when the filter notification was not send by itself
   *
   * @param pData
   */
  handleUpdateFilters(pData: Object): void {
    if (pData.group === FilterGroups.PRICE) return;

    for (const group: Object of pData.data.filterGroups) {
      if (group.type === FilterTypes.RANGE) {
        this.resetFilter(group.filters[0].minimum, group.filters[0].maximum);
        return;
      }
    }
  }

  /**
   * Resets the price filter values to the given values
   */
  resetFilter(min: number, max: number): void {
    const minVal: string = min.toString();
    const maxVal: string = max.toString();

    this.#minValue = +minVal;
    this.#maxValue = +maxVal;

    const elements = [this.#minInput, this.#maxInput, this.#minSlider, this.#maxSlider];
    elements.forEach((element: any, index: number) => {
      element.min = minVal;
      element.max = maxVal;
      index % 2 === 0 ? element.value = minVal : element.value = maxVal;
    });

    this.update(this.low, this.high);

    // Filter logic
    this.#oldValues = [+minVal, +maxVal];
  }

  /**
   * Validates the values of the inputs
   * If needed this method resets the values to a valid value
   */
  validate(): void {
    if (+this.#minInput.value < this.#minValue) {
      this.#minInput.value = this.#minValue.toString();
      this.#minSlider.value = this.#minValue.toString();
    }

    if (+this.#maxInput.value > this.#maxValue || !this.#maxInput.value) {
      this.#maxInput.value = this.#maxValue.toString();
      this.#maxSlider.value = this.#maxValue.toString();
    }
  }

  /**
   * Check if the price filter is active
   */
  isActive(min: number, max: number): boolean {
    return !(min === this.#minValue && max === this.#maxValue);
  }

  /**
   * Check if the price filter has its default values
   */
  hasDefaultValues(min: number, max: number): boolean {
    return min === this.#minValue && max === this.#maxValue;
  }

  /**
   * Check if the price slider has changed
   */
  hasChanged(min: number, max: number): boolean {
    return min !== this.#oldValues[0] || max !== this.#oldValues[1];
  }

  /**
   * Remove all event listeners
   */
  removeEventListeners(): void {
    this.#minSlider.removeEventListener(EventNames.INPUT, this.#updateSliderHandler);
    this.#maxSlider.removeEventListener(EventNames.INPUT, this.#updateSliderHandler);
    this.#minInput.removeEventListener(EventNames.INPUT, this.#numberInputHandler);
    this.#maxInput.removeEventListener(EventNames.INPUT, this.#numberInputHandler);
    this.#minInput.removeEventListener(EventNames.BLUR, this.#numberBlurHandler);
    this.#maxInput.removeEventListener(EventNames.BLUR, this.#numberBlurHandler);

    // Filter handler
    this.#minSlider.removeEventListener(EventNames.CHANGE, this.#filterHandler);
    this.#maxSlider.removeEventListener(EventNames.CHANGE, this.#filterHandler);
  }

  /**
   * Getter for the sliders original value
   */
  get originalValue(): number {
    return parseInt(this.#minSlider.value);
  }

  /**
   * Getter for the sliders lowest value
   */
  get valueLow(): number {
    return Math.min(parseInt(this.#minSlider.value), parseInt(this.#maxSlider.value));
  }

  /**
   * Getter for the sliders highest value
   */
  get valueHigh(): number {
    return Math.max(parseInt(this.#minSlider.value), parseInt(this.#maxSlider.value));
  }

  /**
   * ?
   */
  get low(): number {
    return this.valueLow - this.#minValue;
  }

  /**
   * ?
   */
  get high(): number {
    return this.valueHigh - this.#minValue;
  }

  /**
   * ?
   */
  get value(): string {
    return this.valueLow + ',' + this.valueHigh;
  }

  /**
   * Destroy all event listeners
   */
  destroy(): void {
    this.removeEventListeners();
    super.destroy();
  }
}
